import { Smartphone } from "lucide-react";
import { Contours } from "@/components/Contours";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Device frame sized to the real app screenshots (1290×2796).
 * Pass `src` for a screenshot; omit it to render a designed placeholder.
 */
export const PhoneMockup = ({
  src,
  alt = "",
  label,
  className = "",
  testId,
  loading = "lazy",
  children,
}) => {
  const { t } = useLanguage();
  return (
    <div data-testid={testId} className={`relative ${className}`}>
      {/* side buttons */}
      <div className="absolute -left-[3px] top-[18%] h-[6%] w-[3px] rounded-l-md bg-[#1a1f1a]" aria-hidden="true" />
      <div className="absolute -left-[3px] top-[27%] h-[10%] w-[3px] rounded-l-md bg-[#1a1f1a]" aria-hidden="true" />
      <div className="absolute -right-[3px] top-[22%] h-[14%] w-[3px] rounded-r-md bg-[#1a1f1a]" aria-hidden="true" />

      <div className="relative rounded-[3rem] bg-[#0b0e0b] p-[9px] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9),0_0_0_1px_rgba(243,241,232,0.09)]">
        <div className="pointer-events-none absolute inset-0 rounded-[3rem] ring-1 ring-inset ring-white/[0.05]" />
        {/* dynamic island */}
        <div className="absolute left-1/2 top-[19px] z-20 h-[3%] w-[27%] -translate-x-1/2 rounded-full bg-black" aria-hidden="true" />

        <div className="relative aspect-[1290/2796] overflow-hidden rounded-[2.45rem] bg-[#0f130f]">
          {src ? (
            <img
              src={src}
              alt={alt}
              loading={loading}
              decoding="async"
              className="h-full w-full object-cover"
              draggable="false"
            />
          ) : (
            <div className="relative flex h-full w-full flex-col items-center justify-center bg-gradient-to-b from-[#161c16] via-[#0f140f] to-[#090c09] p-6 text-center">
              <Contours rings={10} step={70} seed={3} cx={640} cy={520} opacity={0.22} />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--olive)]/40 bg-[var(--olive)]/15">
                <Smartphone className="h-7 w-7 text-[var(--sage)]" />
              </div>
              <p className="relative mt-5 font-display text-2xl text-[var(--ink)]">{label}</p>
              <p className="eyebrow relative mt-2 !text-[0.62rem] text-[var(--ink-3)]">
                {t.placeholder.label} · {t.placeholder.note}
              </p>
              <div className="relative mt-8 w-full space-y-2.5 px-3 opacity-60">
                <div className="h-2 w-3/4 rounded-full bg-white/10" />
                <div className="h-2 w-full rounded-full bg-white/10" />
                <div className="h-2 w-2/3 rounded-full bg-white/10" />
              </div>
              {children}
            </div>
          )}
          {/* screen glare */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-white/[0.06]" />
        </div>
      </div>
    </div>
  );
};
